import type {
  AllocationStatus,
  DiscrepancyType,
  JobAllocation,
  ReceivingRecord,
  ReceivingStatus,
  Shipment,
  ShipmentItem,
  ShipmentStatus,
  StockRecord,
} from "@/data/WarehouseInventoryContext";
import type { Job, MaterialLineItem, UnmappedSkuReviewItem } from "@/data/types";
import { hashFilters } from "@/lib/queryKeys";
import { observeQueryStart, recordQueryMetric } from "@/lib/queryObservability";
import { supabase } from "@/lib/supabase";
import { markJobMaterialsInstalled, syncProductRegistryStock, syncStockLevels, syncWarehouseStock } from "@/services/inventory.service";
import { WarehouseService } from "@/services/warehouse.service";
import { LoadoutSyncService } from "@/services/loadoutSync.service";

export type WarehouseShipmentBucket = "incoming" | "completed" | "all";
export type WarehouseReceivingLane = "all" | "pending" | "received_not_allocated" | "issues";
export type WarehouseAllocationLane = "all" | "active" | "reserved" | "ready" | "installed";
export type WarehouseReadinessLane = "all" | "ready" | "not_ready";
export type WarehouseStockLane = "all" | "low" | "over_reserved";
export type WarehouseHubJobLane = "all" | "incoming" | "fully_received" | "ready" | "staged" | "exceptions" | "prep";
export type WarehouseHubStagingLane = "all" | "pending" | "staged";
export type WarehouseHubLoadoutLane = "all" | "pending" | "complete";

export interface PageResponse<T> {
  data: T[];
  count: number;
  error: unknown | null;
}

export interface WarehousePageQuery {
  page: number;
  pageSize: number;
  search?: string;
  refreshToken?: number;
}

export interface WarehouseShipmentsPageQuery extends WarehousePageQuery {
  bucket: WarehouseShipmentBucket;
}

export interface WarehouseReceivingPageQuery extends WarehousePageQuery {
  lane: WarehouseReceivingLane;
}

export interface WarehouseAllocationsPageQuery extends WarehousePageQuery {
  lane: WarehouseAllocationLane;
}

export interface WarehouseReadinessPageQuery extends WarehousePageQuery {
  lane: WarehouseReadinessLane;
}

export interface WarehouseStockPageQuery extends WarehousePageQuery {
  lane: WarehouseStockLane;
}

export interface WarehouseHubJobsPageQuery extends WarehousePageQuery {
  lane: WarehouseHubJobLane;
}

export interface WarehouseHubStagingPageQuery extends WarehousePageQuery {
  lane: WarehouseHubStagingLane;
}

export interface WarehouseHubLoadoutPageQuery extends WarehousePageQuery {
  lane: WarehouseHubLoadoutLane;
}

export interface WarehouseJobMaterialAllocationRow {
  key: string;
  jobId: string;
  jobLabel: string;
  customerName: string;
  projectType: string;
  sku: string;
  productName: string;
  requiredQuantity: number;
  allocatedQuantity: number;
  missingQuantity: number;
  availableStock: number;
  recommendedQuantity: number;
  hasStockMatch: boolean;
}

export interface WarehouseJobReadinessRow {
  jobId: string;
  jobLabel: string;
  customerName: string;
  projectType: string;
  ready: boolean;
  reasons: string[];
}

export interface WarehousePendingSkuApprovalRow {
  reviewId?: string;
  jobId: string;
  jobLabel: string;
  sku: string;
  itemName: string;
  proposedSku?: string;
  key: string;
}

export interface WarehouseHubJobRow {
  id: string;
  jobNumber?: string;
  customerName: string;
  currentStage: Job["currentStage"];
  materialReadiness: Job["materialReadiness"];
  effectiveReadiness: string;
  permitRequired: boolean;
  permitStatus: Job["permitStatus"];
  materialsStatus: Job["materialsStatus"];
  procurementStatus: Job["procurementStatus"];
  warehouseStatus: Job["warehouseStatus"];
  planningStatus?: string;
  jobMaterialStatus?: string;
  assignedCrew: string;
  assignedSubcontractor: string;
  materialReadinessNotes: string;
  missingItemsList: string;
  scheduledInstallDate: string;
  targetInstallDate: string;
  materialsList: MaterialLineItem[];
  metadata: Record<string, unknown>;
  sourceMeta?: Record<string, unknown>;
  materialProgress: number;
}

export interface WarehouseHubStagingItem {
  materialId?: string;
  sku: string;
  itemName: string;
  quantityRequired: number;
  quantityAllocated: number;
  quantityStaged: number;
  missingItems: number;
  fieldPurchase: boolean;
  staged: boolean;
}

export interface WarehouseHubStagingRow {
  jobId: string;
  jobLabel: string;
  customerName: string;
  status: "Not Staged" | "Partially Staged" | "Fully Staged";
  items: WarehouseHubStagingItem[];
  itemCount: number;
  stagedCount: number;
  effectiveReadiness: string;
  assignedCrew: string;
  assignedSubcontractor: string;
}

export interface WarehouseHubLoadoutRow {
  jobId: string;
  jobLabel: string;
  customerName: string;
  complete: boolean;
  allMaterialsLoaded: boolean;
  correctItemsVerified: boolean;
  toolsReady: boolean;
  specialInstructionsReviewed: boolean;
  signedOffBy?: string;
  signedOffAt?: string;
  effectiveReadiness: string;
  assignedCrew: string;
  assignedSubcontractor: string;
}

export interface WarehouseHubSummary {
  activeJobs: number;
  incoming: number;
  fullyReceived: number;
  ready: number;
  staged: number;
  exceptions: number;
  prepTomorrow: number;
  pctReady: number;
  stagingPending: number;
  loadoutPending: number;
}

export interface WarehouseSummary {
  incomingShipments: number;
  completedShipments: number;
  pendingReceiving: number;
  receivedNotAllocated: number;
  allocated: number;
  readyForInstall: number;
  discrepancies: number;
  lowStock: number;
  overReserved: number;
  readinessReady: number;
  readinessNotReady: number;
  pendingSkuApprovals: number;
  allocationOpenRows: number;
  allocationMatchableRows: number;
  allocationUnmatchedRows: number;
  allocationNoAvailableRows: number;
  allocationAllocatedRows: number;
}

type DbShipmentRow = {
  id: string;
  po_id?: string | null;
  po_number?: string | null;
  external_po_number?: string | null;
  vendor_id?: string | null;
  vendor_name?: string | null;
  items?: ShipmentItem[] | null;
  status?: string | null;
  movement_stage?: string | null;
  created_at?: string | null;
  expected_delivery?: string | null;
  delivered_at?: string | null;
  received_at?: string | null;
  warehouse_id?: string | null;
  warehouse_name?: string | null;
};

type DbReceivingRow = {
  id: string;
  po_id?: string | null;
  po_number?: string | null;
  vendor_name?: string | null;
  job_id?: string | null;
  job_label?: string | null;
  sku?: string | null;
  product_name?: string | null;
  expected_quantity?: number | null;
  received_quantity?: number | null;
  status?: string | null;
  received_at?: string | null;
  received_by?: string | null;
  shipment_id?: string | null;
  discrepancy_type?: string | null;
  discrepancy_notes?: string | null;
};

type DbAllocationRow = {
  id: string;
  job_id?: string | null;
  job_label?: string | null;
  sku?: string | null;
  product_name?: string | null;
  quantity?: number | null;
  status?: string | null;
  allocated_at?: string | null;
  allocated_by?: string | null;
  customer_name?: string | null;
  project_type?: string | null;
};

type DbStockRow = {
  sku?: string | null;
  product_name?: string | null;
  current_stock?: number | null;
  reserved_stock?: number | null;
  allocated_stock?: number | null;
  incoming_stock?: number | null;
  available_stock?: number | null;
  reorder_threshold?: number | null;
  has_received?: boolean | null;
};

type DbJobMaterialAllocationRow = {
  row_key?: string | null;
  job_id?: string | null;
  job_label?: string | null;
  customer_name?: string | null;
  project_type?: string | null;
  sku?: string | null;
  product_name?: string | null;
  required_quantity?: number | null;
  allocated_quantity?: number | null;
  missing_quantity?: number | null;
  available_stock?: number | null;
  recommended_quantity?: number | null;
  has_stock_match?: boolean | null;
};

type DbJobReadinessRow = {
  job_id?: string | null;
  job_label?: string | null;
  is_ready?: boolean | null;
  reasons?: string[] | null;
};

type DbPendingSkuApprovalRow = {
  review_id?: string | null;
  approval_key?: string | null;
  job_id?: string | null;
  job_label?: string | null;
  sku?: string | null;
  item_name?: string | null;
  proposed_sku?: string | null;
};

type DbHubJobRow = {
  id?: string | null;
  job_number?: string | null;
  customer_name?: string | null;
  current_stage?: string | null;
  material_readiness?: string | null;
  effective_readiness?: string | null;
  permit_required?: boolean | null;
  permit_status?: string | null;
  materials_status?: string | null;
  procurement_status?: string | null;
  warehouse_status?: string | null;
  planning_status?: string | null;
  job_material_status?: string | null;
  assigned_crew?: string | null;
  assigned_subcontractor?: string | null;
  material_readiness_notes?: string | null;
  missing_items_list?: string | null;
  scheduled_install_date?: string | null;
  target_install_date?: string | null;
  materials_list?: unknown[] | null;
  metadata?: Record<string, unknown> | null;
  source_meta?: Record<string, unknown> | null;
  material_progress?: number | null;
};

type DbHubStagingRow = {
  job_id?: string | null;
  job_label?: string | null;
  customer_name?: string | null;
  status?: string | null;
  items?: unknown[] | null;
  item_count?: number | null;
  staged_count?: number | null;
  effective_readiness?: string | null;
  assigned_crew?: string | null;
  assigned_subcontractor?: string | null;
};

type DbHubLoadoutRow = {
  job_id?: string | null;
  job_label?: string | null;
  customer_name?: string | null;
  complete?: boolean | null;
  all_materials_loaded?: boolean | null;
  correct_items_verified?: boolean | null;
  tools_ready?: boolean | null;
  special_instructions_reviewed?: boolean | null;
  signed_off_by?: string | null;
  signed_off_at?: string | null;
  effective_readiness?: string | null;
  assigned_crew?: string | null;
  assigned_subcontractor?: string | null;
};

const SHIPMENT_COLUMNS = [
  "id",
  "po_id",
  "po_number",
  "external_po_number",
  "vendor_id",
  "vendor_name",
  "items",
  "status",
  "movement_stage",
  "created_at",
  "expected_delivery",
  "delivered_at",
  "received_at",
  "warehouse_id",
  "warehouse_name",
].join(", ");

const RECEIVING_COLUMNS = [
  "id",
  "po_id",
  "po_number",
  "vendor_name",
  "job_id",
  "job_label",
  "sku",
  "product_name",
  "expected_quantity",
  "received_quantity",
  "status",
  "received_at",
  "received_by",
  "shipment_id",
  "discrepancy_type",
  "discrepancy_notes",
].join(", ");

const ALLOCATION_COLUMNS = [
  "id",
  "job_id",
  "job_label",
  "sku",
  "product_name",
  "quantity",
  "status",
  "allocated_at",
  "allocated_by",
  "customer_name",
  "project_type",
].join(", ");

const STOCK_COLUMNS = [
  "sku",
  "product_name",
  "current_stock",
  "reserved_stock",
  "allocated_stock",
  "incoming_stock",
  "available_stock",
  "reorder_threshold",
  "has_received",
].join(", ");

const JOB_MATERIAL_ALLOCATION_COLUMNS = [
  "row_key",
  "job_id",
  "job_label",
  "customer_name",
  "project_type",
  "sku",
  "product_name",
  "required_quantity",
  "allocated_quantity",
  "missing_quantity",
  "available_stock",
  "recommended_quantity",
  "has_stock_match",
].join(", ");

const HUB_JOB_COLUMNS = [
  "id",
  "job_number",
  "customer_name",
  "current_stage",
  "material_readiness",
  "effective_readiness",
  "permit_required",
  "permit_status",
  "materials_status",
  "procurement_status",
  "warehouse_status",
  "planning_status",
  "job_material_status",
  "assigned_crew",
  "assigned_subcontractor",
  "material_readiness_notes",
  "missing_items_list",
  "scheduled_install_date",
  "target_install_date",
  "materials_list",
  "metadata",
  "source_meta",
  "material_progress",
].join(", ");

const HUB_STAGING_COLUMNS = [
  "job_id",
  "job_label",
  "customer_name",
  "status",
  "items",
  "item_count",
  "staged_count",
  "effective_readiness",
  "assigned_crew",
  "assigned_subcontractor",
].join(", ");

const HUB_LOADOUT_COLUMNS = [
  "job_id",
  "job_label",
  "customer_name",
  "complete",
  "all_materials_loaded",
  "correct_items_verified",
  "tools_ready",
  "special_instructions_reviewed",
  "signed_off_by",
  "signed_off_at",
  "effective_readiness",
  "assigned_crew",
  "assigned_subcontractor",
].join(", ");

const WAREHOUSE_REQUEST_CACHE_TTL_MS = 750;
const warehouseRequestCache = new Map<string, { at: number; value: unknown }>();
const warehouseRequestInFlight = new Map<string, Promise<unknown>>();

function clampPage(query: { page: number; pageSize: number }) {
  const page = Math.max(0, Number(query.page) || 0);
  const pageSize = Math.max(1, Math.min(Number(query.pageSize) || 25, 100));
  return { page, pageSize, from: page * pageSize, to: page * pageSize + pageSize - 1 };
}

function sanitizeLike(value: string): string {
  return value.replace(/[%_]/g, "").replace(/,/g, " ").trim();
}

function recordPageMetric(key: string, startedAt: number, payload: unknown) {
  let payloadBytes = 0;
  try {
    payloadBytes = JSON.stringify(payload ?? null).length;
  } catch {
    payloadBytes = 0;
  }
  recordQueryMetric({
    key,
    durationMs: Date.now() - startedAt,
    payloadBytes,
    cacheHit: false,
  });
}

async function withPageMetrics<T>(key: string, run: () => Promise<T>): Promise<T> {
  observeQueryStart(key);
  const startedAt = Date.now();
  try {
    const result = await run();
    recordPageMetric(key, startedAt, result);
    return result;
  } catch (error) {
    recordPageMetric(key, startedAt, { error: String(error) });
    throw error;
  }
}

async function withWarehouseRequest<T>(key: string, run: () => Promise<T>): Promise<T> {
  const now = Date.now();
  const cached = warehouseRequestCache.get(key);
  if (cached && now - cached.at < WAREHOUSE_REQUEST_CACHE_TTL_MS) {
    return cached.value as T;
  }

  const inFlight = warehouseRequestInFlight.get(key);
  if (inFlight) {
    return inFlight as Promise<T>;
  }

  const promise = run().then((result) => {
    warehouseRequestCache.set(key, { at: Date.now(), value: result });
    return result;
  });

  warehouseRequestInFlight.set(key, promise);
  try {
    return await promise;
  } finally {
    warehouseRequestInFlight.delete(key);
  }
}

function mapShipmentStatus(status: unknown): ShipmentStatus {
  const value = String(status ?? "");
  return value === "Ordered" || value === "In Transit" || value === "Delivered" || value === "Received"
    ? value
    : "Ordered";
}

function mapReceivingStatus(status: unknown): ReceivingStatus {
  const value = String(status ?? "");
  return value === "Pending" || value === "Partially Received" || value === "Fully Received"
    ? value
    : "Pending";
}

function mapAllocationStatus(status: unknown): AllocationStatus {
  const value = String(status ?? "");
  return value === "Unallocated" || value === "Reserved" || value === "Ready for Install" || value === "Installed"
    ? value
    : "Unallocated";
}

function mapShipment(row: DbShipmentRow): Shipment {
  const rawItems = row.items;
  const isFullyReceived = String(row.status ?? "") === "Received";
  const parseItems = (raw: unknown): ShipmentItem[] => {
    const arr: ShipmentItem[] = Array.isArray(raw)
      ? (raw as ShipmentItem[])
      : typeof raw === "string"
        ? (() => { try { const p = JSON.parse(raw); return Array.isArray(p) ? (p as ShipmentItem[]) : []; } catch { return []; } })()
        : [];
    // A shipment reaches "Received" only when every receiving record is "Fully Received"
    // (enforced in receiveShipmentItems). The items JSONB stores receivedQuantity = 0 at
    // creation time and is never back-patched, so normalise it here.
    return isFullyReceived
      ? arr.map((item) => ({ ...item, receivedQuantity: item.expectedQuantity }))
      : arr;
  };
  const items = parseItems(rawItems);
  return {
    id: row.id,
    poId: String(row.po_id ?? ""),
    poNumber: String(row.po_number ?? ""),
    externalPoNumber: row.external_po_number || undefined,
    vendorId: String(row.vendor_id ?? ""),
    vendorName: String(row.vendor_name ?? ""),
    items,
    status: mapShipmentStatus(row.status),
    createdAt: String(row.created_at ?? new Date().toISOString()),
    expectedDelivery: row.expected_delivery ?? undefined,
    deliveredAt: row.delivered_at ?? undefined,
    receivedAt: row.received_at ?? undefined,
    warehouseId: row.warehouse_id ?? null,
    warehouseName: row.warehouse_name ?? null,
    movementStage: row.movement_stage ?? null,
  };
}

function mapReceiving(row: DbReceivingRow): ReceivingRecord {
  return {
    id: row.id,
    poId: String(row.po_id ?? ""),
    poNumber: String(row.po_number ?? ""),
    vendorName: String(row.vendor_name ?? ""),
    jobId: row.job_id ?? null,
    jobLabel: row.job_label ?? null,
    sku: String(row.sku ?? ""),
    productName: String(row.product_name ?? ""),
    expectedQuantity: Number(row.expected_quantity ?? 0),
    receivedQuantity: Number(row.received_quantity ?? 0),
    status: mapReceivingStatus(row.status),
    receivedAt: row.received_at ?? undefined,
    receivedBy: row.received_by ?? undefined,
    shipmentId: row.shipment_id ?? undefined,
    allocationBlocked: false,
    auditEvents: [],
    discrepancy: row.discrepancy_type
      ? {
          type: row.discrepancy_type as ReceivingRecord["discrepancy"]["type"],
          notes: String(row.discrepancy_notes ?? ""),
        }
      : undefined,
  };
}

function mapAllocation(row: DbAllocationRow): JobAllocation {
  return {
    id: row.id,
    jobId: String(row.job_id ?? ""),
    jobLabel: String(row.job_label ?? ""),
    sku: String(row.sku ?? ""),
    productName: String(row.product_name ?? ""),
    quantity: Number(row.quantity ?? 0),
    status: mapAllocationStatus(row.status),
    allocatedAt: row.allocated_at ?? undefined,
    allocatedBy: row.allocated_by ?? undefined,
    customerName: row.customer_name ? String(row.customer_name) : undefined,
    projectType: row.project_type ? String(row.project_type) : undefined,
  };
}

function mapStock(row: DbStockRow): StockRecord {
  return {
    sku: String(row.sku ?? ""),
    productName: String(row.product_name ?? row.sku ?? ""),
    currentStock: Number(row.current_stock ?? 0),
    reservedStock: Number(row.reserved_stock ?? 0),
    allocatedStock: Number(row.allocated_stock ?? 0),
    incomingStock: Number(row.incoming_stock ?? 0),
    availableStock: Number(row.available_stock ?? 0),
    reorderThreshold: Number(row.reorder_threshold ?? 0),
    hasReceived: Boolean(row.has_received),
  };
}

function mapJobMaterialAllocation(row: DbJobMaterialAllocationRow): WarehouseJobMaterialAllocationRow {
  return {
    key: String(row.row_key ?? `${row.job_id}:${row.sku}`),
    jobId: String(row.job_id ?? ""),
    jobLabel: String(row.job_label ?? row.job_id ?? ""),
    customerName: String(row.customer_name ?? ""),
    projectType: String(row.project_type ?? ""),
    sku: String(row.sku ?? ""),
    productName: String(row.product_name ?? row.sku ?? ""),
    requiredQuantity: Number(row.required_quantity ?? 0),
    allocatedQuantity: Number(row.allocated_quantity ?? 0),
    missingQuantity: Number(row.missing_quantity ?? 0),
    availableStock: Number(row.available_stock ?? 0),
    recommendedQuantity: Number(row.recommended_quantity ?? 0),
    hasStockMatch: Boolean(row.has_stock_match),
  };
}

function mapHubMaterialItem(value: unknown): WarehouseHubStagingItem {
  const row = (value ?? {}) as Record<string, unknown>;
  return {
    materialId: row.materialId ? String(row.materialId) : undefined,
    sku: String(row.sku ?? ""),
    itemName: String(row.itemName ?? row.sku ?? ""),
    quantityRequired: Number(row.quantityRequired ?? 0),
    quantityAllocated: Number(row.quantityAllocated ?? 0),
    quantityStaged: Number(row.quantityStaged ?? 0),
    missingItems: Number(row.missingItems ?? 0),
    fieldPurchase: Boolean(row.fieldPurchase),
    staged: Boolean(row.staged),
  };
}

function mapHubJob(row: DbHubJobRow): WarehouseHubJobRow {
  const effectiveReadiness = String(row.effective_readiness ?? row.material_readiness ?? "Not Ordered");
  return {
    id: String(row.id ?? ""),
    jobNumber: row.job_number ?? undefined,
    customerName: String(row.customer_name ?? "Unknown"),
    currentStage: String(row.current_stage ?? "Sale Closed") as Job["currentStage"],
    materialReadiness: effectiveReadiness as Job["materialReadiness"],
    effectiveReadiness,
    permitRequired: Boolean(row.permit_required),
    permitStatus: String(row.permit_status ?? "Not Required") as Job["permitStatus"],
    materialsStatus: String(row.materials_status ?? "Not Ready") as Job["materialsStatus"],
    procurementStatus: String(row.procurement_status ?? "Not Ordered") as Job["procurementStatus"],
    warehouseStatus: String(row.warehouse_status ?? "Not Received") as Job["warehouseStatus"],
    planningStatus: row.planning_status ?? undefined,
    jobMaterialStatus: row.job_material_status ?? undefined,
    assignedCrew: String(row.assigned_crew ?? ""),
    assignedSubcontractor: String(row.assigned_subcontractor ?? ""),
    materialReadinessNotes: String(row.material_readiness_notes ?? ""),
    missingItemsList: String(row.missing_items_list ?? ""),
    scheduledInstallDate: String(row.scheduled_install_date ?? ""),
    targetInstallDate: String(row.target_install_date ?? ""),
    materialsList: Array.isArray(row.materials_list)
      ? row.materials_list.map((item) => mapHubMaterialItem(item) as unknown as MaterialLineItem)
      : [],
    metadata: row.metadata ?? {},
    sourceMeta: row.source_meta ?? undefined,
    materialProgress: Number(row.material_progress ?? 0),
  };
}

function mapHubStaging(row: DbHubStagingRow): WarehouseHubStagingRow {
  const status = String(row.status ?? "Not Staged");
  return {
    jobId: String(row.job_id ?? ""),
    jobLabel: String(row.job_label ?? row.job_id ?? ""),
    customerName: String(row.customer_name ?? ""),
    status: (status === "Partially Staged" || status === "Fully Staged" ? status : "Not Staged") as WarehouseHubStagingRow["status"],
    items: Array.isArray(row.items) ? row.items.map(mapHubMaterialItem) : [],
    itemCount: Number(row.item_count ?? 0),
    stagedCount: Number(row.staged_count ?? 0),
    effectiveReadiness: String(row.effective_readiness ?? ""),
    assignedCrew: String(row.assigned_crew ?? ""),
    assignedSubcontractor: String(row.assigned_subcontractor ?? ""),
  };
}

function mapHubLoadout(row: DbHubLoadoutRow): WarehouseHubLoadoutRow {
  return {
    jobId: String(row.job_id ?? ""),
    jobLabel: String(row.job_label ?? row.job_id ?? ""),
    customerName: String(row.customer_name ?? ""),
    complete: Boolean(row.complete),
    allMaterialsLoaded: Boolean(row.all_materials_loaded),
    correctItemsVerified: Boolean(row.correct_items_verified),
    toolsReady: Boolean(row.tools_ready),
    specialInstructionsReviewed: Boolean(row.special_instructions_reviewed),
    signedOffBy: row.signed_off_by ?? undefined,
    signedOffAt: row.signed_off_at ?? undefined,
    effectiveReadiness: String(row.effective_readiness ?? ""),
    assignedCrew: String(row.assigned_crew ?? ""),
    assignedSubcontractor: String(row.assigned_subcontractor ?? ""),
  };
}

function applySearch(query: any, search: string | undefined, columns: string[]) {
  const term = sanitizeLike(String(search ?? ""));
  if (!term) return query;
  return query.or(columns.map((column) => `${column}.ilike.%${term}%`).join(","));
}

async function fetchJobMetadata(jobId: string): Promise<Record<string, unknown>> {
  const { data, error } = await supabase
    .from("jobs")
    .select("metadata")
    .eq("id", jobId)
    .maybeSingle();
  if (error) {
    console.warn("WarehousePageService.fetchJobMetadata failed:", error);
    return {};
  }
  return ((data as { metadata?: Record<string, unknown> | null } | null)?.metadata ?? {}) as Record<string, unknown>;
}

export const WarehousePageService = {
  async getShipmentsPage(query: WarehouseShipmentsPageQuery): Promise<PageResponse<Shipment>> {
    const { from, to } = clampPage(query);
    const key = `warehouse.shipments.${hashFilters(query)}`;
    try {
      return await withWarehouseRequest(key, () => withPageMetrics(key, async () => {
        let request = supabase
          .from("v_warehouse_shipments_ui")
          .select(SHIPMENT_COLUMNS, { count: "exact" })
          .order("sort_at", { ascending: false, nullsFirst: false })
          .range(from, to);
        if (query.bucket === "incoming") request = request.eq("is_incoming", true);
        if (query.bucket === "completed") request = request.eq("is_completed", true);
        request = applySearch(request, query.search, ["po_number", "vendor_name"]);
        const { data, error, count } = await request;
        if (error) throw error;
        return { data: ((data ?? []) as DbShipmentRow[]).map(mapShipment), count: count ?? 0, error: null };
      }));
    } catch (error) {
      console.warn("WarehousePageService.getShipmentsPage failed:", error);
      return { data: [], count: 0, error };
    }
  },

  async getReceivingPage(query: WarehouseReceivingPageQuery): Promise<PageResponse<ReceivingRecord>> {
    const { from, to } = clampPage(query);
    const key = `warehouse.receiving.${hashFilters(query)}`;
    try {
      return await withWarehouseRequest(key, () => withPageMetrics(key, async () => {
        let request = supabase
          .from("v_warehouse_receiving_ui")
          .select(RECEIVING_COLUMNS, { count: "exact" })
          .order("sort_at", { ascending: false, nullsFirst: false })
          .range(from, to);
        if (query.lane === "pending") request = request.eq("is_pending", true);
        if (query.lane === "received_not_allocated") request = request.eq("is_received_not_allocated", true);
        if (query.lane === "issues") request = request.eq("has_discrepancy", true);
        // Hide installed rows from the main received table — once all allocations are
        // marked Installed there is nothing left to act on for that record.
        if (query.lane === "all") request = request.eq("is_installed", false);
        request = applySearch(request, query.search, ["po_number", "vendor_name", "job_label", "sku", "product_name"]);
        const { data, error, count } = await request;
        if (error) throw error;
        return { data: ((data ?? []) as DbReceivingRow[]).map(mapReceiving), count: count ?? 0, error: null };
      }));
    } catch (error) {
      console.warn("WarehousePageService.getReceivingPage failed:", error);
      return { data: [], count: 0, error };
    }
  },

  async getAllocationsPage(query: WarehouseAllocationsPageQuery): Promise<PageResponse<JobAllocation>> {
    const { from, to } = clampPage(query);
    const key = `warehouse.allocations.${hashFilters(query)}`;
    try {
      return await withWarehouseRequest(key, () => withPageMetrics(key, async () => {
        let request = supabase
          .from("v_warehouse_allocations_ui")
          .select(ALLOCATION_COLUMNS, { count: "exact" })
          .order("sort_at", { ascending: false, nullsFirst: false })
          .range(from, to);
        if (query.lane === "active") request = request.or("is_active.eq.true,status.eq.Installed");
        if (query.lane === "reserved") request = request.eq("is_reserved", true);
        if (query.lane === "ready") request = request.eq("is_ready_for_install", true);
        if (query.lane === "installed") request = request.eq("status", "Installed");
        request = applySearch(request, query.search, ["job_label", "sku", "product_name"]);
        const { data, error, count } = await request;
        if (error) throw error;
        return { data: ((data ?? []) as DbAllocationRow[]).map(mapAllocation), count: count ?? 0, error: null };
      }));
    } catch (error) {
      console.warn("WarehousePageService.getAllocationsPage failed:", error);
      return { data: [], count: 0, error };
    }
  },

  async getStockPage(query: WarehouseStockPageQuery): Promise<PageResponse<StockRecord>> {
    const { from, to } = clampPage(query);
    const key = `warehouse.stock.${hashFilters(query)}`;
    try {
      return await withWarehouseRequest(key, () => withPageMetrics(key, async () => {
        let request = supabase
          .from("v_warehouse_stock_ui")
          .select(STOCK_COLUMNS, { count: "exact" })
          // Hide phantom rows but keep genuinely received SKUs even at zero stock.
          // has_received = true means this SKU was physically received — it stays visible
          // after load-out even when current_stock hits 0.
          .or("current_stock.gt.0,reserved_stock.gt.0,incoming_stock.gt.0,has_received.eq.true")
          .order("sort_at", { ascending: false, nullsFirst: false })
          .range(from, to);
        if (query.lane === "low") request = request.eq("is_low_stock", true);
        if (query.lane === "over_reserved") request = request.eq("is_over_reserved", true);
        request = applySearch(request, query.search, ["sku", "product_name"]);
        const { data, error, count } = await request;
        if (error) throw error;
        return { data: ((data ?? []) as DbStockRow[]).map(mapStock), count: count ?? 0, error: null };
      }));
    } catch (error) {
      console.warn("WarehousePageService.getStockPage failed:", error);
      return { data: [], count: 0, error };
    }
  },

  async getJobMaterialAllocationPage(query: WarehousePageQuery & { filter?: "required" | "available" | "missing" | "allocated" }): Promise<PageResponse<WarehouseJobMaterialAllocationRow>> {
    const { from, to } = clampPage(query);
    const key = `warehouse.job-material-allocation.${hashFilters(query)}`;
    try {
      return await withWarehouseRequest(key, () => withPageMetrics(key, async () => {
        let request = supabase
          .from("v_warehouse_job_material_allocation_ui")
          .select(JOB_MATERIAL_ALLOCATION_COLUMNS, { count: "exact" })
          .order("sort_at", { ascending: false, nullsFirst: false })
          .range(from, to);
        switch (query.filter) {
          case "available":
            request = request.gt("missing_quantity", 0).gt("recommended_quantity", 0);
            break;
          case "missing":
            request = request.gt("missing_quantity", 0).lte("recommended_quantity", 0);
            break;
          case "allocated":
            request = request.lte("missing_quantity", 0).gt("allocated_quantity", 0);
            break;
          default:
            request = request.gt("missing_quantity", 0);
        }
        request = applySearch(request, query.search, ["job_label", "customer_name", "sku", "product_name"]);
        const { data, error, count } = await request;
        if (error) throw error;
        return { data: ((data ?? []) as DbJobMaterialAllocationRow[]).map(mapJobMaterialAllocation), count: count ?? 0, error: null };
      }));
    } catch (error) {
      console.warn("WarehousePageService.getJobMaterialAllocationPage failed:", error);
      return { data: [], count: 0, error };
    }
  },

  async getReadinessPage(query: WarehouseReadinessPageQuery): Promise<PageResponse<WarehouseJobReadinessRow>> {
    const { from, to } = clampPage(query);
    const key = `warehouse.readiness.${hashFilters(query)}`;
    try {
      return await withWarehouseRequest(key, () => withPageMetrics(key, async () => {
        let request = supabase
          .from("v_warehouse_job_readiness_ui")
          .select("job_id, job_label, customer_name, project_type, is_ready, reasons", { count: "exact" })
          .order("sort_at", { ascending: false, nullsFirst: false })
          .range(from, to);
        if (query.lane === "ready") request = request.eq("is_ready", true);
        if (query.lane === "not_ready") request = request.eq("is_ready", false);
        request = applySearch(request, query.search, ["job_label"]);
        const { data, error, count } = await request;
        if (error) throw error;
        return {
          data: ((data ?? []) as DbJobReadinessRow[]).map((row) => ({
            jobId: String(row.job_id ?? ""),
            jobLabel: String(row.job_label ?? row.job_id ?? ""),
            customerName: String((row as any).customer_name ?? ""),
            projectType: String((row as any).project_type ?? ""),
            ready: Boolean(row.is_ready),
            reasons: Array.isArray(row.reasons) ? row.reasons.map(String) : [],
          })),
          count: count ?? 0,
          error: null,
        };
      }));
    } catch (error) {
      console.warn("WarehousePageService.getReadinessPage failed:", error);
      return { data: [], count: 0, error };
    }
  },

  async getPendingSkuApprovalsPage(query: WarehousePageQuery): Promise<PageResponse<WarehousePendingSkuApprovalRow>> {
    const { from, to } = clampPage(query);
    const key = `warehouse.pending-sku-approvals.${hashFilters(query)}`;
    try {
      return await withWarehouseRequest(key, () => withPageMetrics(key, async () => {
        let request = supabase
          .from("v_warehouse_pending_sku_approvals_ui")
          .select("review_id, approval_key, job_id, job_label, sku, item_name, proposed_sku", { count: "exact" })
          .order("sort_at", { ascending: false, nullsFirst: false })
          .range(from, to);
        request = applySearch(request, query.search, ["job_label", "sku", "item_name", "proposed_sku"]);
        const { data, error, count } = await request;
        if (error) throw error;
        return {
          data: ((data ?? []) as DbPendingSkuApprovalRow[]).map((row) => ({
            reviewId: row.review_id ?? undefined,
            jobId: String(row.job_id ?? ""),
            jobLabel: String(row.job_label ?? row.job_id ?? ""),
            sku: String(row.sku ?? ""),
            itemName: String(row.item_name ?? row.sku ?? ""),
            proposedSku: row.proposed_sku ?? undefined,
            key: String(row.approval_key ?? `${row.job_id}:${row.sku}:${row.item_name}`),
          })),
          count: count ?? 0,
          error: null,
        };
      }));
    } catch (error) {
      console.warn("WarehousePageService.getPendingSkuApprovalsPage failed:", error);
      return { data: [], count: 0, error };
    }
  },

  async getSummary(refreshToken = 0): Promise<{ data: WarehouseSummary | null; error: unknown | null }> {
    const key = `warehouse.summary.${refreshToken}`;
    try {
      return await withWarehouseRequest(key, () => withPageMetrics(key, async () => {
        const { data, error } = await supabase
          .from("v_warehouse_summary")
          .select("*")
          .limit(1)
          .maybeSingle();
        if (error) throw error;
        const row = (data ?? {}) as Record<string, unknown>;
        return {
          data: {
            incomingShipments: Number(row.incoming_shipments ?? 0),
            completedShipments: Number(row.completed_shipments ?? 0),
            pendingReceiving: Number(row.pending_receiving ?? 0),
            receivedNotAllocated: Number(row.received_not_allocated ?? 0),
            allocated: Number(row.allocated ?? 0),
            readyForInstall: Number(row.ready_for_install ?? 0),
            discrepancies: Number(row.discrepancies ?? 0),
            lowStock: Number(row.low_stock ?? 0),
            overReserved: Number(row.over_reserved ?? 0),
            readinessReady: Number(row.readiness_ready ?? 0),
            readinessNotReady: Number(row.readiness_not_ready ?? 0),
            pendingSkuApprovals: Number(row.pending_sku_approvals ?? 0),
            allocationOpenRows: Number(row.allocation_open_rows ?? 0),
            allocationMatchableRows: Number(row.allocation_matchable_rows ?? 0),
            allocationUnmatchedRows: Number(row.allocation_unmatched_rows ?? 0),
            allocationNoAvailableRows: Number(row.allocation_no_available_rows ?? 0),
            allocationAllocatedRows: Number(row.allocation_allocated_rows ?? 0),
          },
          error: null,
        };
      }));
    } catch (error) {
      console.warn("WarehousePageService.getSummary failed:", error);
      return { data: null, error };
    }
  },

  async getHubJobsPage(query: WarehouseHubJobsPageQuery): Promise<PageResponse<WarehouseHubJobRow>> {
    const { from, to } = clampPage(query);
    const key = `warehouse.hub.jobs.${hashFilters(query)}`;
    try {
      return await withWarehouseRequest(key, () => withPageMetrics(key, async () => {
        let request = supabase
          .from("v_warehouse_hub_jobs_ui")
          .select(HUB_JOB_COLUMNS, { count: "exact" })
          .eq("is_active", true)
          .order("sort_at", { ascending: false, nullsFirst: false })
          .range(from, to);
        if (query.lane === "incoming") request = request.eq("is_incoming", true);
        if (query.lane === "fully_received") request = request.eq("is_fully_received", true);
        if (query.lane === "ready") request = request.eq("is_ready", true);
        if (query.lane === "staged") request = request.eq("is_staged", true);
        if (query.lane === "exceptions") request = request.eq("is_exception", true);
        if (query.lane === "prep") request = request.eq("is_prep_tomorrow", true);
        request = applySearch(request, query.search, [
          "job_number",
          "customer_name",
          "effective_readiness",
          "assigned_crew",
          "assigned_subcontractor",
          "missing_items_list",
          "material_readiness_notes",
        ]);
        const { data, error, count } = await request;
        if (error) throw error;
        return { data: ((data ?? []) as DbHubJobRow[]).map(mapHubJob), count: count ?? 0, error: null };
      }));
    } catch (error) {
      console.warn("WarehousePageService.getHubJobsPage failed:", error);
      return { data: [], count: 0, error };
    }
  },

  async getHubStagingPage(query: WarehouseHubStagingPageQuery): Promise<PageResponse<WarehouseHubStagingRow>> {
    const { from, to } = clampPage(query);
    const key = `warehouse.hub.staging.${hashFilters(query)}`;
    try {
      return await withWarehouseRequest(key, () => withPageMetrics(key, async () => {
        let request = supabase
          .from("v_warehouse_hub_staging_ui")
          .select(HUB_STAGING_COLUMNS, { count: "exact" })
          .order("sort_at", { ascending: false, nullsFirst: false })
          .range(from, to);
        if (query.lane === "pending") request = request.neq("status", "Fully Staged");
        if (query.lane === "staged") request = request.eq("status", "Fully Staged");
        request = applySearch(request, query.search, ["job_label", "customer_name", "status", "effective_readiness"]);
        const { data, error, count } = await request;
        if (error) throw error;
        return { data: ((data ?? []) as DbHubStagingRow[]).map(mapHubStaging), count: count ?? 0, error: null };
      }));
    } catch (error) {
      console.warn("WarehousePageService.getHubStagingPage failed:", error);
      return { data: [], count: 0, error };
    }
  },

  async getHubLoadoutPage(query: WarehouseHubLoadoutPageQuery): Promise<PageResponse<WarehouseHubLoadoutRow>> {
    const { from, to } = clampPage(query);
    const key = `warehouse.hub.loadout.${hashFilters(query)}`;
    try {
      return await withWarehouseRequest(key, () => withPageMetrics(key, async () => {
        let request = supabase
          .from("v_warehouse_hub_loadout_ui")
          .select(HUB_LOADOUT_COLUMNS, { count: "exact" })
          .order("sort_at", { ascending: false, nullsFirst: false })
          .range(from, to);
        if (query.lane === "pending") request = request.eq("complete", false);
        if (query.lane === "complete") request = request.eq("complete", true);
        request = applySearch(request, query.search, ["job_label", "customer_name", "effective_readiness", "assigned_crew", "assigned_subcontractor"]);
        const { data, error, count } = await request;
        if (error) throw error;
        return { data: ((data ?? []) as DbHubLoadoutRow[]).map(mapHubLoadout), count: count ?? 0, error: null };
      }));
    } catch (error) {
      console.warn("WarehousePageService.getHubLoadoutPage failed:", error);
      return { data: [], count: 0, error };
    }
  },

  async getHubSummary(refreshToken = 0): Promise<{ data: WarehouseHubSummary | null; error: unknown | null }> {
    const key = `warehouse.hub.summary.${refreshToken}`;
    try {
      return await withWarehouseRequest(key, () => withPageMetrics(key, async () => {
        const { data, error } = await supabase
          .from("v_warehouse_hub_summary")
          .select("*")
          .limit(1)
          .maybeSingle();
        if (error) throw error;
        const row = (data ?? {}) as Record<string, unknown>;
        return {
          data: {
            activeJobs: Number(row.active_jobs ?? 0),
            incoming: Number(row.incoming ?? 0),
            fullyReceived: Number(row.fully_received ?? 0),
            ready: Number(row.ready ?? 0),
            staged: Number(row.staged ?? 0),
            exceptions: Number(row.exceptions ?? 0),
            prepTomorrow: Number(row.prep_tomorrow ?? 0),
            pctReady: Number(row.pct_ready ?? 0),
            stagingPending: Number(row.staging_pending ?? 0),
            loadoutPending: Number(row.loadout_pending ?? 0),
          },
          error: null,
        };
      }));
    } catch (error) {
      console.warn("WarehousePageService.getHubSummary failed:", error);
      return { data: null, error };
    }
  },

  async markHubJobStaged(row: WarehouseHubStagingRow): Promise<boolean> {
    const jobId = row.jobId.trim();
    if (!jobId) return false;

    let ok = true;
    for (const item of row.items) {
      const materialId = String(item.materialId ?? "").trim();
      if (!materialId) continue;
      const { error } = await supabase
        .from("job_materials")
        .update({ quantity_staged: Math.max(0, Math.round(item.quantityRequired)) })
        .eq("id", materialId);
      if (error) {
        ok = false;
        console.warn("WarehousePageService.markHubJobStaged material update failed:", error);
      }
    }

    const metadata = await fetchJobMetadata(jobId);
    const nextMetadata = {
      ...metadata,
      materialReadiness: "Staged for Job",
      warehouseStatus: "Staged",
      materialReadinessNotes: "",
      missingItemsList: "",
    };
    const { error } = await supabase
      .from("jobs")
      .update({
        material_readiness: "Staged for Job",
        metadata: nextMetadata,
        updated_at: new Date().toISOString(),
      })
      .eq("id", jobId);
    if (error) {
      ok = false;
      console.warn("WarehousePageService.markHubJobStaged job update failed:", error);
    }
    return ok;
  },

  async markHubJobLoaded(jobId: string, signedOffBy?: string): Promise<boolean> {
    const id = jobId.trim();
    if (!id) return false;
    const signedOffAt = new Date().toISOString();
    const metadata = await fetchJobMetadata(id);
    const nextMetadata = {
      ...metadata,
      materialReadiness: "Loaded for Crew",
      warehouseStatus: "Loaded",
      loadOutChecklist: {
        allMaterialsLoaded: true,
        correctItemsVerified: true,
        toolsReady: true,
        specialInstructionsReviewed: true,
        complete: true,
        signedOffBy: String(signedOffBy ?? "Warehouse").trim() || "Warehouse",
        signedOffAt,
      },
    };
    const { error } = await supabase
      .from("jobs")
      .update({
        material_readiness: "Loaded for Crew",
        metadata: nextMetadata,
        updated_at: signedOffAt,
      })
      .eq("id", id);
    if (error) {
      console.warn("WarehousePageService.markHubJobLoaded failed:", error);
      return false;
    }

    // Notify the Portal that materials are now loaded (state → "delivered").
    void LoadoutSyncService.triggerLoadoutPush(id, "loadout.updated");

    // Decrement product_registry.stock_quantity by the allocated quantity per SKU,
    // then remove the allocation rows so the view doesn't double-count the reduction.
    const { data: materialRows, error: materialsError } = await supabase
      .from("job_materials")
      .select("sku, quantity_allocated")
      .eq("job_id", id)
      .gt("quantity_allocated", 0);

    if (!materialsError && materialRows && materialRows.length > 0) {
      await Promise.all(
        materialRows.flatMap((row: { sku: string; quantity_allocated: number }) => [
          syncProductRegistryStock(row.sku, { quantityOnHandDelta: -row.quantity_allocated }),
          syncStockLevels(row.sku, { quantityOnHandDelta: -row.quantity_allocated }),
          syncWarehouseStock(row.sku, { quantityOnHandDelta: -row.quantity_allocated }),
        ]),
      );

      // Mark job_materials rows as installed in DB.
      // Do NOT delete job_allocations — Installed rows are the audit trail that
      // materials were handled, and they keep is_installed = true on receiving records
      // so those records don't reappear in the received table as needing allocation.
      await markJobMaterialsInstalled(id);
    }

    return true;
  },

  async createPendingSkuReviews(params: {
    organizationId: string;
    jobId: string;
    jobLabel: string;
    items: UnmappedSkuReviewItem[];
    source?: string;
  }): Promise<boolean> {
    const organizationId = params.organizationId.trim();
    const jobId = params.jobId.trim();
    if (!organizationId || !jobId || params.items.length === 0) return true;

    let ok = true;
    for (const item of params.items) {
      const sku = String(item.sku ?? "").trim();
      const itemName = String(item.itemName ?? "").trim();
      if (!sku || !itemName) continue;

      const { error } = await supabase
        .from("warehouse_sku_reviews")
        .insert({
          organization_id: organizationId,
          job_id: jobId,
          job_label: params.jobLabel || jobId,
          sku,
          item_name: itemName,
          proposed_sku: item.proposedSku ?? null,
          source: params.source ?? "migration",
          metadata: { legacy: item },
        });

      if (error && error.code !== "23505") {
        ok = false;
        console.warn("WarehousePageService.createPendingSkuReviews failed:", error);
      }
    }
    return ok;
  },

  async resolvePendingSkuReview(
    reviewId: string | undefined,
    status: "approved" | "rejected",
    resolutionSku?: string,
  ): Promise<boolean> {
    const id = String(reviewId ?? "").trim();
    if (!id) return false;

    const { error } = await supabase
      .from("warehouse_sku_reviews")
      .update({
        status,
        resolution_sku: resolutionSku ?? null,
        resolved_at: new Date().toISOString(),
        resolved_by: "Warehouse",
      })
      .eq("id", id);

    if (error) {
      console.warn("WarehousePageService.resolvePendingSkuReview failed:", error);
      return false;
    }
    return true;
  },

  async receiveRecord(
    record: ReceivingRecord,
    quantityReceived: number,
    discrepancy?: { type: DiscrepancyType; notes: string },
  ): Promise<boolean> {
    const nextReceived = Math.min(record.expectedQuantity, record.receivedQuantity + Math.max(0, quantityReceived));
    const status: ReceivingStatus = nextReceived >= record.expectedQuantity ? "Fully Received" : "Partially Received";
    return WarehouseService.updateReceivingRecord(record.id, {
      receivedQuantity: nextReceived,
      status,
      receivedAt: new Date().toISOString(),
      receivedBy: "Warehouse",
      ...(discrepancy ? { discrepancy } : {}),
    });
  },

  async receiveShipmentItems(
    shipmentId: string,
    receipts: { sku: string; received: number; discrepancy?: { type: DiscrepancyType; notes: string } }[],
  ): Promise<boolean> {
    const { data, error } = await supabase
      .from("receiving_records")
      .select(RECEIVING_COLUMNS)
      .eq("shipment_id", shipmentId);
    if (error) {
      console.warn("WarehousePageService.receiveShipmentItems failed to load receiving rows:", error);
      return false;
    }

    let rows = (data ?? []) as DbReceivingRow[];

    // Fallback: if no records are linked by shipment_id (e.g. created before
    // the vendor UUID issue was fixed), look up via the PO that owns this shipment.
    if (rows.length === 0) {
      const { data: shipmentRow } = await supabase
        .from("shipments")
        .select("po_id")
        .eq("id", shipmentId)
        .single();
      if (shipmentRow?.po_id) {
        const { data: fallbackRows } = await supabase
          .from("receiving_records")
          .select(RECEIVING_COLUMNS)
          .eq("po_id", shipmentRow.po_id)
          .is("shipment_id", null);
        if ((fallbackRows ?? []).length > 0) {
          rows = fallbackRows as DbReceivingRow[];
          // Back-link the orphaned records to this shipment so future calls work correctly.
          await supabase
            .from("receiving_records")
            .update({ shipment_id: shipmentId })
            .eq("po_id", shipmentRow.po_id)
            .is("shipment_id", null);
        }
      }
    }
    // Run all receive-record writes in parallel — each operates on a unique recordId
    // and SKU, so there are no cross-item conflicts on the core DB update.
    // syncJobMaterialReadiness / rebuildJobMaterialsList may race for the same job_id
    // when multiple SKUs share a job, but the reconcile step below corrects the final
    // aggregate state, making that race safe.
    const receiveResults = await Promise.all(
      receipts
        .filter((r) => r.received > 0)
        .map((receipt) => {
          const row = rows.find((candidate) =>
            String(candidate.sku ?? "") === receipt.sku &&
            String(candidate.status ?? "") !== "Fully Received"
          );
          if (!row) return Promise.resolve(true);
          return WarehousePageService.receiveRecord(mapReceiving(row), receipt.received, receipt.discrepancy);
        })
    );
    let coreOk = receiveResults.every(Boolean);

    // Reconcile allocations + auto-promote in the background — these do not gate
    // the shipment status update and must not block the UI response.
    const affectedJobIds = Array.from(
      new Set(rows.map((r) => String(r.job_id ?? "").trim()).filter((id) => id.length > 0))
    );
    const receivedSkus = receipts
      .filter((r) => r.received > 0)
      .map((r) => String(r.sku ?? "").trim())
      .filter((s) => s.length > 0);

    void Promise.all(
      affectedJobIds.map(async (jobId) => {
        try {
          await WarehouseService.reconcileJobAllocationsFromReceiving(jobId);
        } catch (err) {
          console.warn("[WarehousePageService] Allocation reconcile failed after shipment receiving:", err);
        }
        if (receivedSkus.length > 0) {
          try {
            await supabase
              .from("job_allocations")
              .update({ status: "Ready for Install", updated_at: new Date().toISOString() })
              .eq("job_id", jobId)
              .eq("status", "Reserved")
              .in("sku", receivedSkus);
          } catch (err) {
            console.warn("[WarehousePageService] Auto-promote Reserved→Ready for Install failed:", err);
          }
        }
      })
    );

    const latest = await supabase
      .from("receiving_records")
      .select("status")
      .eq("shipment_id", shipmentId);
    const allReceived = !latest.error && (latest.data ?? []).length > 0 && (latest.data ?? []).every((row) => row.status === "Fully Received");
    if (allReceived) {
      // skipJobSync=true: reconciliation + readiness already ran above in the
      // receiveRecord loop, so we only need to update shipment + PO status here.
      coreOk = (await WarehouseService.updateShipmentStatus(shipmentId, "Received", undefined, true)) && coreOk;
    }
    return coreOk;
  },

  async saveJobPrepChecklist(
    jobId: string,
    checks: { staged?: boolean; loaded?: boolean; missing?: boolean },
  ): Promise<boolean> {
    const id = String(jobId ?? "").trim();
    if (!id) return false;

    const { data: existing, error: fetchError } = await supabase
      .from("jobs")
      .select("metadata")
      .eq("id", id)
      .single();

    if (fetchError) {
      console.warn("[WarehousePageService] saveJobPrepChecklist fetch failed:", fetchError);
      return false;
    }

    const currentMeta = (existing?.metadata ?? {}) as Record<string, unknown>;
    const { error } = await supabase
      .from("jobs")
      .update({ metadata: { ...currentMeta, prepChecklist: checks } })
      .eq("id", id);

    if (error) {
      console.warn("[WarehousePageService] saveJobPrepChecklist update failed:", error);
      return false;
    }
    return true;
  },
};
